import React, { useEffect, useRef, useState } from "react";
import ChatWrapper from "./ChatWrapper";
import styled from "styled-components";
import {
  getAnyProfileDetails,
  getProfileDetails,
  getallMessages,
} from "../utilities/ApiCalls";
import Scrollbars from "react-custom-scrollbars";
import {
  Dot,
  SendHorizonal,
  Trash2,
  Edit3,
  MoreHorizontal,
} from "lucide-react";
import {
  useLocation,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import axios from "axios";
import Constant from "../Constant";
import { io } from "socket.io-client";
import { useTranslation } from "react-i18next";

var socket, selectedChatCompare;

const Message = () => {
  const [message, setMessage] = useState("");
  const [userMessages, setUserMessages] = useState([]);
  const [socketConnected, setSocketConnected] = useState(false);
  const { userId, id } = useParams();
  const location = useLocation();
  const [create, setCreate] = useState(false);
  const [t] = useTranslation();

  const [roomJoined, setRoomJoined] = useState(false);
  const [myProfile, setMyProfile] = useState({});
  const [roomId, setRoom] = useState("");
  const [connected, setConnecting] = useState(false);
  const [userLive, setUserLive] = useState(false);
  const [profile, setProfile] = useState({});
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [openActionsForId, setOpenActionsForId] = useState(null);
  const FIVE_MINUTES_MS = 5 * 60 * 1000;

  const scrollbarsRef = useRef(null);
  const inputRef = useRef(null);

  // Function to mark messages as read
  const markMessagesAsRead = async (chatId) => {
    try {
      let token = localStorage.getItem("loginDetails");
      const response = await axios.post(
        Constant.apiBasePath + "message/mark-read",
        { chatId: chatId },
        { headers: { token: token } }
      );

      // console.log("Mark read response:", response.data);

      if (response.data.success && response.data.markedCount > 0) {
        // console.log(`Marked ${response.data.markedCount} messages as read`);
        // Emit socket event to notify other users that messages were read
        if (socket && myProfile._id) {
          socket.emit("messages_read", {
            chatId: chatId,
            userId: myProfile._id,
            markedCount: response.data.markedCount,
          });
        }
        // Refresh messages to get updated readBy status
        setTimeout(() => GetAllChatMessages(), 500);
      }
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  useEffect(() => {
    if (scrollbarsRef.current) {
      scrollbarsRef.current.scrollToBottom();
    }
  }, [id, message]);

  const myId = () => {
    getProfileDetails().then((response) => {
      setMyProfile(response.data.data);
      return response.data;
    });
  };

  useEffect(() => {
    myId();
  }, [userId]);

  useEffect(() => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  }, []);

  useEffect(() => {
    socket = io("https://api.storytent.in");
    socket.on("connect", () => {
      // console.log("socket connected");
      // console.log("roomId", socket.id);
      socket.emit("join chat", id);
      setRoomJoined(true);
      setConnecting(false);
      // console.log("room joined");
      setSocketConnected(true);
    });

    socket.on("message", (data) => {
      // console.log("data from socket io", data);
      setUserMessages((prevMessages) => [...prevMessages, data]);

      // Auto-mark new messages as read if they're not from me and I'm viewing this chat
      if (
        data.sender._id !== myProfile._id &&
        myProfile._id &&
        document.hasFocus() &&
        !document.hidden
      ) {
        setTimeout(() => markMessagesAsRead(id), 1000);
      }
    });

    // Reflect deletions pushed from other clients
    socket.on("message_deleted", (payload) => {
      if (!payload || !payload.messageId) return;
      setUserMessages((prev) =>
        prev.map((m) =>
          m._id === payload.messageId ? { ...m, isDeleted: true } : m
        )
      );
    });

    // Listen for when someone reads messages
    socket.on("messages_read", (data) => {
      // console.log(
      //   "Messages were read by:",
      //   data.userId,
      //   "Count:",
      //   data.markedCount
      // );
      // Only refresh if the reader is not me (to avoid infinite loops)
      if (data.userId !== myProfile._id) {
        GetAllChatMessages();
      }
    });

    socket.on("disconnect", () => {
      // console.log("socket disconnected", socket.id);
      setSocketConnected(false);
    });

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [id, userId, myProfile._id]); // Added myProfile._id to dependencies

  useEffect(() => {
    getAnyProfileDetails(userId).then((response) => {
      // console.log("profile", response.data.data);
      setProfile(response.data.data);
    });
  }, [userId]);

  useEffect(() => {
    if (scrollbarsRef.current) {
      scrollbarsRef.current.scrollToBottom();
    }
  }, [userMessages]);

  useEffect(() => {
    if (id) {
      GetAllChatMessages();
    }
  }, [id]);

  // Mark messages as read when user focuses on the window/tab or when chat opens
  useEffect(() => {
    let markReadTimeout;

    const handleFocus = () => {
      if (id && myProfile._id) {
        clearTimeout(markReadTimeout);
        markReadTimeout = setTimeout(() => markMessagesAsRead(id), 500);
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && id && myProfile._id) {
        clearTimeout(markReadTimeout);
        markReadTimeout = setTimeout(() => markMessagesAsRead(id), 500);
      }
    };

    // Mark as read when component mounts and user profile is loaded
    if (id && myProfile._id) {
      clearTimeout(markReadTimeout);
      markReadTimeout = setTimeout(() => markMessagesAsRead(id), 1000);
    }

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearTimeout(markReadTimeout);
    };
  }, [id, myProfile._id]);

  const GetAllChatMessages = async () => {
    if (!id) {
      // console.log("roomId is not set");
      return;
    }
    try {
      const response = await getallMessages(id);
      if (response && response.data) {
        // Handle both old and new response formats
        let messages, markedAsRead;

        if (response.data.messages) {
          // New format with markedAsRead info
          messages = response.data.messages;
          markedAsRead = response.data.markedAsRead;
        } else {
          // Old format - just messages array
          messages = response.data;
          markedAsRead = null;
        }

        // Do not exclude soft-deleted messages. They will render with a placeholder.
        setUserMessages(messages || []);

        // If messages were auto-marked as read by backend, emit socket event
        if (markedAsRead && markedAsRead.count > 0 && socket && myProfile._id) {
          // console.log(
          //   `Auto-emitting socket event: ${markedAsRead.count} messages marked as read`
          // );
          socket.emit("messages_read", {
            chatId: markedAsRead.chatId,
            userId: markedAsRead.userId,
            markedCount: markedAsRead.count,
          });
        }
      }
    } catch (error) {
      // console.log("Error fetching messages:", error);
    }
  };

  const canModifyMessage = (msg) => {
    if (!msg || !msg.createdAt) return false;
    const created = new Date(msg.createdAt).getTime();
    return Date.now() - created <= FIVE_MINUTES_MS;
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const token = localStorage.getItem("loginDetails");
      await axios.delete(`${Constant.apiBasePath}message/delete/${messageId}`, {
        headers: { token },
      });
      // Optimistically mark as deleted in local state without full reload
      setUserMessages((prev) =>
        prev.map((m) => (m._id === messageId ? { ...m, isDeleted: true } : m))
      );
      // Optionally notify via socket so the other side updates
      if (socket) {
        socket.emit("message_deleted", { messageId, chatId: id });
      }
    } catch (err) {
      console.error("Delete message failed", err);
    }
  };

  const handleEditClick = (msg) => {
    setEditingMessageId(msg._id);
    setMessage(msg.content || "");
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  async function createMessage() {
    if (!message.trim()) return;

    try {
      const token = localStorage.getItem("loginDetails");

      // If editing, call edit API and update local state
      if (editingMessageId) {
        const resp = await axios.put(
          `${Constant.apiBasePath}message/edit/${editingMessageId}`,
          { content: message.trim() },
          { headers: { token } }
        );

        const updated = resp.data?.message || resp.data;
        if (updated && updated._id) {
          setUserMessages((prev) =>
            prev.map((m) => (m._id === updated._id ? { ...m, ...updated } : m))
          );
        }

        // Clear editing state and input
        setEditingMessageId(null);
        setMessage("");
        return resp;
      }

      // Otherwise create a new message
      const response = await axios.post(
        Constant.apiBasePath + "message",
        { content: message, chatId: id },
        { headers: { token } }
      );

      if (socket) {
        socket.emit("message", response.data);
        setMessage("");
      } else {
        // console.log("Socket not connected");
      }

      setCreate(!create);
      setMessage("");
      return response;
    } catch (error) {
      console.error("Error sending message:", error);
    }
  }

  // console.log("userprofile ", userId, "myprofile", myProfile._id);

  // Show seen status for messages sent by me
  const isMessageSeen = (messageData, messageIndex) => {
    const isMyMessage = messageData.sender._id === myProfile._id;

    if (!isMyMessage) return false;

    // Check if this message has been read by the other person
    const hasBeenSeenByOtherPerson =
      messageData.readBy &&
      messageData.readBy.some(
        (readerId) =>
          readerId === profile._id ||
          (typeof readerId === "object" && readerId._id === profile._id)
      );

    if (!hasBeenSeenByOtherPerson) return false;

    // Find the LAST message sent by me that was actually SEEN by the other person
    let lastSeenMessageByMeIndex = -1;
    for (let i = userMessages.length - 1; i >= 0; i--) {
      const msg = userMessages[i];
      const isMyMsg = msg.sender._id === myProfile._id;
      const isSeenByOther =
        msg.readBy &&
        msg.readBy.some(
          (readerId) =>
            readerId === profile._id ||
            (typeof readerId === "object" && readerId._id === profile._id)
        );

      if (isMyMsg && isSeenByOther) {
        lastSeenMessageByMeIndex = i;
        break;
      }
    }

    // Check if there are any messages from the other person AFTER my last seen message
    const hasOtherPersonRepliedAfterSeeing = userMessages
      .slice(lastSeenMessageByMeIndex + 1)
      .some((msg) => msg.sender._id === profile._id);

    // Only show "seen" if:
    // 1. This is my last seen message
    // 2. The other person hasn't sent any message after seeing my messages
    return (
      messageIndex === lastSeenMessageByMeIndex &&
      !hasOtherPersonRepliedAfterSeeing
    );
  };
  const proxiedUrl = (url) => {
    if (!url || url.includes("story-tent-testing.s3.ap-south-1.amazonaws.com")) return url;
    return `${Constant.apiBasePath}proxy-image?url=${encodeURIComponent(url)}`;
  };
  return (
    <ChatWrapper>
      <Root>
        <div className="header">
          <img
            alt=""
            className="profile-img"
            src={proxiedUrl(profile.profilePic)}
          ></img>
          <div className="name">{profile.name}</div>
          {userLive && (
            <div className="status">
              <Dot></Dot>live
            </div>
          )}
          {socketConnected && <div className="connection-status">●</div>}
        </div>
        <div className="hr"></div>
        <div className="messages">
          <Scrollbars
            ref={scrollbarsRef}
            className="scrollbar"
            renderThumbVertical={() => <div style={{ display: "none" }} />}
            renderTrackVertical={() => <div style={{ display: "none" }} />}
            renderThumbHorizontal={() => <div style={{ display: "none" }} />}
            renderTrackHorizontal={() => <div style={{ display: "none" }} />}
            renderView={(props) => (
              <div
                {...props}
                style={{
                  ...props.style,
                  overflowX: "hidden",
                  marginRight: 0,
                  marginBottom: 0,
                }}
              />
            )}
          >
            {userMessages.map((data, index) => {
              const isCondition =
                data.sender._id === myProfile._id ? true : false;
              const showEdit =
                isCondition &&
                !data.isDeleted &&
                !data.isEdited &&
                canModifyMessage(data);
              const showDelete =
                isCondition && !data.isDeleted && canModifyMessage(data);

              return (
                <div
                  key={index}
                  className={`message-box ${isCondition ? "position" : ""}`}
                >
                  <div className="message-content">
                    <div className="message-line">
                      {(showEdit || showDelete) && (
                        <div
                          className={`message-actions ${
                            openActionsForId === data._id ? "open" : ""
                          }`}
                        >
                          <button
                            aria-label="Message actions"
                            title="More"
                            onClick={() =>
                              setOpenActionsForId(
                                openActionsForId === data._id ? null : data._id
                              )
                            }
                            className="actions-trigger"
                          >
                            <MoreHorizontal size={16} />
                          </button>
                          {openActionsForId === data._id && (
                            <div
                              className={`actions-menu ${
                                isCondition ? "align-right" : ""
                              }`}
                            >
                              {showEdit && (
                                <button
                                  aria-label="Edit message"
                                  title="Edit"
                                  onClick={() => {
                                    setOpenActionsForId(null);
                                    handleEditClick(data);
                                  }}
                                  className="action-btn edit"
                                >
                                  <Edit3 size={16} />
                                </button>
                              )}
                              {showDelete && (
                                <button
                                  aria-label="Delete message"
                                  title="Delete"
                                  onClick={() => {
                                    setOpenActionsForId(null);
                                    handleDeleteMessage(data._id);
                                  }}
                                  className="action-btn delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      <span className={`msg ${isCondition ? "msg-col" : ""}`}>
                        {data.isDeleted ? (
                          <span className="deleted-text">
                            this message was deleted
                          </span>
                        ) : (
                          data.content
                        )}
                      </span>
                    </div>
                    {data.isEdited && <div className="edited-text">edited</div>}
                    {editingMessageId === data._id && (
                      <div className="editing-indicator">editing...</div>
                    )}
                    {isMessageSeen(data, index) && (
                      <div className="seen-text">Seen</div>
                    )}
                  </div>
                </div>
              );
            })}
          </Scrollbars>
        </div>
        <form
          className="input-container"
          onSubmit={(e) => {
            e.preventDefault();
            createMessage();
          }}
        >
          <textarea
            ref={inputRef}
            onChange={(e) => setMessage(e.target.value)}
            value={message}
            placeholder={t("Chat.msghere")}
            className="textarea"
            rows="1"
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                createMessage();
              }
            }}
          ></textarea>
          <button type="submit" className="send" disabled={!message.trim()}>
            <SendHorizonal></SendHorizonal>
          </button>
        </form>
      </Root>
    </ChatWrapper>
  );
};

export default Message;

const Root = styled.div`
  padding: 20px;
  width: 100%;
  height: 100%;
  max-width: 1200px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  /* Global scrollbar hiding for all elements */
  * {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */

    &::-webkit-scrollbar {
      display: none !important; /* Chrome, Safari, Opera */
      width: 0 !important;
      height: 0 !important;
    }

    &::-webkit-scrollbar-track {
      display: none !important;
    }

    &::-webkit-scrollbar-thumb {
      display: none !important;
    }

    &::-webkit-scrollbar-corner {
      display: none !important;
    }
  }

  .msg {
    background-color: white;
    padding: 5px;
    max-width: 85%;
    height: auto;
    word-wrap: break-word;
    padding-left: 10px;
    padding-right: 10px;
    border-radius: 10px;
  }

  .input-container {
    width: 98%;
    padding-top: 6px;
    padding-bottom: 6px;
    padding-left: 4px;
    padding-right: 4px;
    min-width: 20px;
    margin-top: 20px;
    position: relative;
    background-color: white;
    border-radius: 20px;
    margin-left: 15px;
  }

  .textarea {
    width: 100%;
    min-height: 24px;
    max-height: 100px;
    border-radius: 20px;
    border: none;
    outline: none;
    padding: 8px 36px 8px 8px;
    resize: none;
    font-size: 14px;
    line-height: 1.5;
    overflow-y: auto;
    background: transparent;

    /* Extra specific scrollbar hiding for textarea */
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;

    &::-webkit-scrollbar {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
      background: transparent !important;
    }

    &:hover::-webkit-scrollbar {
      display: none !important;
    }

    &:focus::-webkit-scrollbar {
      display: none !important;
    }
  }

  .send {
    position: absolute;
    color: black;
    right: 12px;
    border-radius: 5px;
    outline: none;
    border: none;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white;
    font-weight: bold;
    width: 32px;
    height: 32px;
    padding: 0;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .position {
    justify-content: end;
    padding-right: 30px;
  }

  .scrollbar {
    height: 100%;

    /* Hide all scrollbar elements completely */
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;

    &::-webkit-scrollbar {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
    }

    &::-webkit-scrollbar-track {
      display: none !important;
    }

    &::-webkit-scrollbar-thumb {
      display: none !important;
    }

    &::-webkit-scrollbar-corner {
      display: none !important;
    }

    &:hover::-webkit-scrollbar {
      display: none !important;
    }

    /* Hide scrollbars for the react-custom-scrollbars component */
    div[style*="overflow"] {
      scrollbar-width: none !important;
      -ms-overflow-style: none !important;

      &::-webkit-scrollbar {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
      }
    }
  }

  .messages {
    height: 100%;
    margin-top: -20px;
    padding-right: 20px;

    /* Allow action menus to overflow beyond the container without clipping */
    overflow: visible;

    @media (min-width: 1400px) {
      height: 90%;
    }
  }

  .name {
    color: white;
  }

  .header {
    display: flex;
    gap: 10px;
    width: 100%;
    padding-left: 10px;
    padding-top: 10px;
    align-items: center;
  }

  .message-box {
    padding: 10px;
    display: flex;
    width: auto;
    height: auto;
    padding-left: 30px;
  }

  .message-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }

  .position .message-content {
    align-items: flex-end;
  }

  .message-line {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    width: 100%;
  }

  .position .message-line {
    justify-content: flex-end;
  }

  .message-actions {
    display: flex;
    gap: 6px;
    align-items: center;
    opacity: 0;
    visibility: hidden;
    transition: opacity 120ms ease;
  }

  /* Show actions when hovering a message */
  .message-content:hover .message-actions {
    opacity: 1;
    visibility: visible;
  }

  /* Keep visible when menu is open */
  .message-actions.open {
    opacity: 1;
    visibility: visible;
  }

  .actions-trigger {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    color: #666;
  }

  .actions-menu {
    display: inline-flex;
    gap: 6px;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    padding: 4px 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    margin-left: 4px;
    z-index: 2;
  }

  .actions-menu.align-right {
    margin-right: 4px;
  }

  .action-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 2px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .action-btn.delete {
    color: #c00;
  }

  .deleted-text {
    color: #888;
    font-style: italic;
  }

  .seen-text {
    font-size: 12px;
    color: #888;
    margin-top: 4px;
    margin-right: 10px;
    font-style: italic;
  }

  .edited-text {
    font-size: 12px;
    color: #888;
    margin-top: 4px;
    margin-right: 10px;
    font-style: italic;
  }

  .editing-indicator {
    font-size: 12px;
    color: #555;
    margin-top: 2px;
    margin-right: 10px;
    font-style: italic;
  }

  .connection-status {
    color: #4caf50;
    font-size: 12px;
    margin-left: auto;
  }

  .hr {
    width: 100%;
    margin-top: 10px;
    border: none;
    border: 1px solid white;
    margin-bottom: 20px;
    opacity: 0.5;
  }

  .profile-img {
    width: 30px;
    height: 30px;
    border-radius: 100px;
  }

  margin: 0;
  padding: 0;
`;
